import IProject from '../../types/IProject';
import IQuestionnaire from '../../types/survey/IQuestionnaire';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { createSelector } from 'reselect';
import { AppState } from '../../reducers/root.reducer';
import { LOGOUT } from '../../constants/actions/auth.constants';

const NAME = 'DashboardSlice';

export type DashboardState = {
    project: IProject | null;
    questionnaire: IQuestionnaire | null;
    consent: {
        visible: boolean;
        sent: boolean;
    };
    language: {
        visible: boolean;
    };
};

const initialState: DashboardState = {
    project: null,
    questionnaire: null,
    consent: {
        visible: false,
        sent: false,
    },
    language: {
        visible: false,
    },
};

type DialogPayload = {
    project: IProject;
    questionnaire: IQuestionnaire;
};

const slice = createSlice({
    name: NAME,
    initialState,
    reducers: createReducers(),
    extraReducers: (builder) => {
        builder.addCase(LOGOUT, () => initialState);
    },
});

function createReducers() {
    return {
        showConsent: (
            state: DashboardState,
            action: PayloadAction<DialogPayload>,
        ) => {
            state.consent.visible = true;
            state.consent.sent = false;
            state.language.visible = false;
            loadDialog(state, action);
        },
        showLanguage: (
            state: DashboardState,
            action: PayloadAction<DialogPayload>,
        ) => {
            state.language.visible = true;
            state.consent.visible = false;
            loadDialog(state, action);
        },
        closeDialog: () => initialState,
        confirmConsentDialog: (state: DashboardState) => {
            state.consent.visible = false;
            state.consent.sent = true;
            state.language.visible = false;
        },
        confirmLanguageDialog: (state: DashboardState) => {
            state.consent.visible = state.project?.consent === false;
            state.language.visible = false;
        },
        setProject: (
            state: DashboardState,
            action: PayloadAction<{ project: IProject }>,
        ) => {
            state.project = action.payload.project;
        },
        clear: () => initialState,
    };

    function loadDialog(
        state: DashboardState,
        action: PayloadAction<DialogPayload>,
    ) {
        state.project = action.payload.project;
        state.questionnaire = action.payload.questionnaire;
    }
}

const state = (state: AppState) => state.participant.dashboard;

const selectQuestionnaire = createSelector(
    state,
    (state: DashboardState) => state.questionnaire,
);

const DashboardSlice = {
    actions: slice.actions,
    reducer: slice.reducer,
    state: {
        all: state,
        questionnaireHasSupportedLanguage: createSelector(
            [
                selectQuestionnaire,
                (_state: AppState, locale?: string) => locale,
            ],
            (questionnaire: IQuestionnaire | null, locale?: string) =>
                !!(
                    locale &&
                    questionnaire?.languages?.includes(locale.toLowerCase())
                ),
        ),
    },
};

export default DashboardSlice;
